import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronUp, ChevronDown, ShoppingBag, ArrowRight, Bell, List, 
  Eye, Heart, Star, Filter, Download, Bookmark, ExternalLink, Clock, 
  TrendingUp, Users, Zap, RefreshCw, MoreHorizontal, Copy, CheckCircle,
  DollarSign, Calendar, Network, Layers, Wallet, Activity as ActivityIcon,
  ArrowLeftRight, CheckCircle as CheckCircleIcon, Plus, Tag, 
  Package, Gift, Award, ArrowLeftRight as Swap, X, Image as ImageIcon, Box, ChevronRight, AlertCircle
} from 'lucide-react';
import { useActivity, ACTIVITY_TYPES } from '../contexts/ActivityContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useFavorites } from '../hooks/useFavorites';
import { getAllMarketplaceActivities, enrichActivitiesWithPrice, filterActivitiesByType, filterActivitiesByTime } from '../utils/activityUtils';
import { supabase } from '../lib/supabase';
import ModelPreview from '../components/ModelPreview';
import ModelViewer from '../components/ModelViewer.jsx';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import RequestTradeModal from '../components/RequestTradeModal';
import { getCategoryDisplayName } from '../utils/pieceUtils';
import { normalizePieces } from '../utils/pieceNormalizer.js';
import { getCapsuleForPiece } from '../utils/supabaseDatabase';
import { useWallet } from '../contexts/WalletContext';
import { checkSufficientBalance, canWalletTransact } from '../utils/marketplaceTransactions';
import { purchasePiece } from '../utils/editionPurchase';
import { formatSOLPrice, formatUSDPrice } from '../utils/priceFormatting';
import { useWalletAction } from '../hooks/useWalletAction';
import { createOffer } from '../utils/offersContract';
import { 
  getActiveListing, 
  canListPiece, 
  canPurchaseListing,
  subscribeToListing,
  subscribeToPiece
} from '../utils/resaleMarketplace';
import { trackPieceView } from '../utils/supabaseViewTracking';
import VerifiedBadge from '../components/VerifiedBadge';

const Activity = () => {
  const navigate = useNavigate();
  
  // Get SOL price from context
  const { solPrice } = useSolPrice();
  const { formatTimeAgo, exportActivities, logSaleFeeCollected } = useActivity();
  const { toggleFavorite, isFavorited } = useFavorites();
  const { isConnected, balance, publicKey, wallet } = useWallet();
  const { withWalletCheck } = useWalletAction();

  // Helper function to truncate addresses for mobile
  const truncateAddress = (address) => {
    if (!address) return '';
    if (address.length <= 12) return address;
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Helper function to copy address to clipboard
  const copyAddress = (address) => {
    if (!address) return;
    navigator.clipboard.writeText(address).then(() => {
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 2000);
    }).catch(err => {
      console.error('Failed to copy address:', err);
    });
  };

  // State management
  const [selectedEventTypes, setSelectedEventTypes] = useState(['all']);
  const [selectedView, setSelectedView] = useState('marketplace'); // marketplace, collection, account
  const [isLive, setIsLive] = useState(true);
  const [allActivities, setAllActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bookmarkedViews, setBookmarkedViews] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [showThumbnail, setShowThumbnail] = useState(false);
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState(Date.now());
  const [currentOffset, setCurrentOffset] = useState(0);
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null); // Track capsule info for current piece
  
  // Buying functionality state
  const [buyModal, setBuyModal] = useState({ isOpen: false, item: null });
  const [offerModal, setOfferModal] = useState({ 
    isOpen: false, 
    item: null, 
    offerAmount: '', 
    expirationDays: 7, 
    expirationHours: 0 
  });
  const [requestTradeModal, setRequestTradeModal] = useState({ isOpen: false, item: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  const [activeListing, setActiveListing] = useState(null);
  const [listingLoading, setListingLoading] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Edition selector state (for multiple quantity pieces)
  const [groupedPieceIds, setGroupedPieceIds] = useState([]);
  const [allEditions, setAllEditions] = useState([]);
  const [selectedEditionIndex, setSelectedEditionIndex] = useState(0);
  const [showEditionSelector, setShowEditionSelector] = useState(false);
  
  const observerRef = useRef();
  const lastTrackedPiece = useRef(null);

  // Handle multiple event type selection
  const toggleEventType = (eventType) => {
    setSelectedEventTypes(prev => {
      if (eventType === 'all') {
        // If selecting 'all', clear all other selections
        return ['all'];
      } else {
        // If selecting a specific type, remove 'all' and toggle the type
        const newSelection = prev.filter(type => type !== 'all');
        if (newSelection.includes(eventType)) {
          // Remove the type if it's already selected
          const updated = newSelection.filter(type => type !== eventType);
          // If no types left, select 'all'
          return updated.length === 0 ? ['all'] : updated;
        } else {
          // Add the type
          return [...newSelection, eventType];
        }
      }
    });
  };

  // Event types with icons and colors - using ACTIVITY_TYPES from context
  const eventTypes = {
    all: { label: 'All Events', icon: ActivityIcon, color: 'text-white', bgColor: 'bg-gray-500/20' },
    PIECE_CREATED: { label: 'Piece Minted', icon: Plus, color: 'text-white', bgColor: 'bg-yellow-500/20' },
    PIECE_SOLD: { label: 'Piece Sold', icon: ShoppingBag, color: 'text-white', bgColor: 'bg-green-500/20' },
    PIECE_LISTED: { label: 'Piece Listed', icon: Tag, color: 'text-white', bgColor: 'bg-blue-500/20' },
    PIECE_TRADED: { label: 'Trade Executed', icon: Swap, color: 'text-white', bgColor: 'bg-purple-500/20' },
    COLLECTION_CREATED: { label: 'Capsule Minted', icon: Package, color: 'text-white', bgColor: 'bg-cyan-500/20' },
  };





  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const iconMap = {
      'Plus': Plus,
      'ShoppingBag': ShoppingBag,
      'Tag': Tag,
      'Swap': Swap,
      'Package': Package,
      'Gift': Gift,
      'Award': Award,
      'Users': Users,
      'Wallet': Wallet,
      'Activity': ActivityIcon
    };
    return iconMap[iconName] || ActivityIcon;
  };

  // Fetch activities from backend
  const fetchActivities = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('🔄 Fetching marketplace activities...');
      
      // Fetch all activities from backend (reset to offset 0)
      let activities = await getAllMarketplaceActivities(100, 0);
      
      console.log('📊 Total activities fetched:', activities.length);
      
      // Count activities by type
      const typeCounts = activities.reduce((acc, activity) => {
        acc[activity.type] = (acc[activity.type] || 0) + 1;
        return acc;
      }, {});
      console.log('📊 Activities by type:', typeCounts);
      
      // Log sample activity to debug images
      if (activities.length > 0) {
        console.log('📊 Sample activity data:', activities[0]);
        console.log('📊 Most recent 3 activities:', activities.slice(0, 3).map(a => ({
          type: a.type,
          pieceName: a.pieceName,
          timeAgo: a.timeAgo,
          from: a.from?.slice(0, 8),
          to: a.to?.slice(0, 8)
        })));
      }
      
      // Enrich with real-time SOL price
      if (solPrice) {
        activities = await enrichActivitiesWithPrice(activities, solPrice);
      }
      
      setAllActivities(activities);
      setCurrentOffset(100); // Set offset for next load
      setHasMoreActivities(activities.length === 100); // If we got less than 100, no more to load
      setLastFetchTime(Date.now());
      
      console.log(`✅ Loaded ${activities.length} activities`);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setIsLoading(false);
    }
  }, [solPrice]);

  // Debounce timer ref for infinite scroll
  const loadMoreTimerRef = useRef(null);
  
  // Load more activities (infinite scroll) with debouncing
  const loadMoreActivities = useCallback(async () => {
    if (isLoadingMore || !hasMoreActivities) return;
    
    // Debounce to prevent rapid successive requests
    if (loadMoreTimerRef.current) {
      return; // Already have a pending request
    }
    
    loadMoreTimerRef.current = setTimeout(async () => {
      setIsLoadingMore(true);
      
      try {
        console.log(`🔄 Loading more activities from offset ${currentOffset}...`);
        
        // Fetch more activities with current offset
        const moreActivities = await getAllMarketplaceActivities(100, currentOffset);
        
        // Enrich with SOL price
        let enrichedActivities = moreActivities;
        if (solPrice) {
          enrichedActivities = await enrichActivitiesWithPrice(moreActivities, solPrice);
        }
        
        // If we got fewer than 100 activities, we've reached the end
        if (enrichedActivities.length < 100) {
          setHasMoreActivities(false);
        }
        
        // Add new activities that aren't already in the list (deduplicate)
        const existingIds = new Set(allActivities.map(a => a.id));
        const newActivities = enrichedActivities.filter(a => !existingIds.has(a.id));
        
        if (newActivities.length === 0) {
          setHasMoreActivities(false);
        } else {
          setAllActivities(prev => [...prev, ...newActivities]);
          setCurrentOffset(prev => prev + 100); // Increment offset for next load
          console.log(`✅ Loaded ${newActivities.length} new activities`);
        }
      } catch (error) {
        console.error('Error loading more activities:', error);
      } finally {
        setIsLoadingMore(false);
        loadMoreTimerRef.current = null;
      }
    }, 300); // 300ms debounce
  }, [isLoadingMore, hasMoreActivities, allActivities, solPrice, currentOffset]);

  // Memoize filtered activities to prevent unnecessary recalculations
  const memoizedFilteredActivities = useMemo(() => {
    let filtered = [...allActivities];
    
    // Filter out offer-related activities
    filtered = filtered.filter(activity => 
      activity.type !== 'OFFER_MADE' && activity.type !== 'OFFER_ACCEPTED'
    );
    
    // Apply type filter
    filtered = filterActivitiesByType(filtered, selectedEventTypes);
    
    // Apply time filter (if needed)
    // filtered = filterActivitiesByTime(filtered, 'all');
    
    // Update time ago for all activities
    filtered = filtered.map(activity => ({
      ...activity,
      timeAgo: formatTimeAgo(activity.timestamp)
    }));

    return filtered;
  }, [allActivities, selectedEventTypes, formatTimeAgo]);
  
  // Implement windowing: only render a subset of activities for performance
  // This prevents the DOM from having thousands of rows
  const MAX_RENDERED_ACTIVITIES = 200; // Render max 200 rows at a time
  const displayedActivities = useMemo(() => {
    // Only render the first MAX_RENDERED_ACTIVITIES for initial display
    // Infinite scroll will load more as needed
    return memoizedFilteredActivities.slice(0, MAX_RENDERED_ACTIVITIES);
  }, [memoizedFilteredActivities]);
  
  // Filter activities based on current filters (kept for backward compatibility)
  const filterActivities = useCallback(() => {
    setFilteredActivities(memoizedFilteredActivities);
  }, [memoizedFilteredActivities]);

  // Copy address to clipboard
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAddress(type);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Handle buy now button
  const handleBuyNow = withWalletCheck((item) => {
    console.log('💰 Opening buy modal for item:', {
      id: item?.id,
      name: item?.name,
      price: item?.price,
      listing_price: item?.listing_price,
      lowestPrice: item?.lowestPrice,
      listing_status: item?.listing_status
    });
    setBuyModal({ isOpen: true, item });
  }, 'make a purchase');

  // Process purchase with wallet - REAL BLOCKCHAIN TRANSACTION
  const processPurchase = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    // Check wallet capability
    if (!canWalletTransact(wallet)) {
      showNotification('Please use Phantom or Solflare wallet to make purchases.', 'error');
      return;
    }

    const { item } = buyModal;
    // Use listing_price first, fallback to price
    const price = parseFloat(item.listing_price || item.price || '0');
    
    if (!price || price <= 0) {
      showNotification('Cannot purchase piece with invalid price. Please refresh and try again.', 'error');
      closeBuyModal();
      return;
    }
    
    // Check sufficient balance (with buffer for gas fees)
    const balanceCheck = await checkSufficientBalance({ connected: isConnected, publicKey }, price);
    if (!balanceCheck.sufficient) {
      showNotification(`Not enough SOL. You need ${balanceCheck.required.toFixed(2)} SOL but only have ${balanceCheck.balance.toFixed(2)} SOL`, 'error');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Execute the complete purchase flow on blockchain using edition-aware purchase
      console.log(`🛒 Purchasing ${item.name} for ${price} SOL...`);
      console.log(`🎨 Edition system: Auto-selecting available edition if multi-edition piece`);
      
      // Use edition-aware purchase (automatically selects available edition)
      const result = await purchasePiece(
        wallet,
        item,
        price
      );
      
      // Log edition info if available
      if (result.editionNumber && result.totalEditions) {
        console.log(`✅ Purchased edition ${result.editionNumber}/${result.totalEditions}`);
      }
      
      // Log sale fee activity
      logSaleFeeCollected({
        pieceId: item.id,
        pieceName: item.name,
        feeAmount: result.paymentTransaction?.feeBreakdown?.feeAmount || result.feeBreakdown?.feeAmount || 0,
        piecePrice: price,
        buyerWallet: publicKey.toString(),
        sellerWallet: item.holder_wallet || item.creators?.wallet || 'Unknown Seller',
        transactionHash: result.paymentTransaction?.signature || result.transactionSignature
      });
      
      showNotification(
        `Successfully purchased ${item.name} for ${price.toFixed(2)} SOL! 🎉`,
        'success'
      );
      
      // Close modal
      setBuyModal({ isOpen: false, item: null });
      
      // Reload to show updated ownership
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      // Make error messages user-friendly
      let userMessage = 'Unable to complete purchase. Please try again.';
      if (error.message?.includes('insufficient')) {
        userMessage = 'Not enough SOL in your wallet to complete this purchase.';
      } else if (error.message?.includes('rejected') || error.message?.includes('declined')) {
        userMessage = 'Purchase cancelled. You declined the transaction.';
      } else if (error.message?.includes('delegate') || error.message?.includes('delegated')) {
        userMessage = 'This piece is not properly listed for sale. The seller needs to re-list it. Please try a different piece.';
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      showNotification(userMessage, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Close buy modal
  const closeBuyModal = () => {
    setBuyModal({ isOpen: false, item: null });
  };

  // Handle purchase listing from secondary marketplace
  const handlePurchaseListing = withWalletCheck(async (item) => {
    if (!activeListing) {
      showNotification('No active listing found', 'error');
      return;
    }
    
    try {
      setListingLoading(true);
      console.log('🛒 Purchasing:', item.name);
      console.log('🎨 Edition system: Auto-selecting available edition if multi-edition piece');
      
      // Use edition-aware purchase (automatically selects available edition)
      const result = await purchasePiece(
        wallet, 
        item, 
        activeListing.price_sol
      );
      
      console.log('✅ Purchase successful:', result);
      
      // Log edition info if available
      const editionInfo = result.editionNumber && result.totalEditions 
        ? ` | Edition: ${result.editionNumber}/${result.totalEditions}` 
        : '';
      
      showNotification(
        `Successfully purchased ${item.name}!${editionInfo} 🎉`,
        'success'
      );
      
      closePieceDetailModal();
      
      // Reload to show updated ownership
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      showNotification('Unable to complete purchase. Please try again.', 'error');
    } finally {
      setListingLoading(false);
    }
  }, 'purchase this piece');

  // Handle make offer button
  const handleMakeOffer = withWalletCheck(async (item) => {
    // Check if user already has an active offer on this piece
    try {
      const { getPieceOffers } = await import('../utils/offersContract');
      const offersResult = await getPieceOffers(item.mint_address, 'active');
      
      if (offersResult.success && offersResult.offers.length > 0) {
        // Check if any of the active offers are from this user
        const userOffer = offersResult.offers.find(
          offer => offer.offerer_wallet === publicKey?.toString()
        );
        
        if (userOffer) {
          showNotification(
            `You already have an active offer of ${userOffer.offer_price} SOL on this piece. Visit the Offers page to view or update it.`,
            'info'
          );
          return;
        }
      }
    } catch (error) {
      console.log('Could not check for existing offers:', error);
      // Continue anyway - the backend will catch duplicates
    }
    
    setOfferModal({ isOpen: true, item, offerAmount: '', expirationDays: 7, expirationHours: 0 });
  }, 'make an offer');

  // Handle request trade button
  const handleRequestTrade = withWalletCheck((item) => {
    setRequestTradeModal({ isOpen: true, item });
  }, 'request a trade');

  // Handle trade request submission
  const handleTradeRequestSubmit = (tradeRequest) => {
    console.log('Trade request submitted:', tradeRequest);
    // Close both the trade modal and the piece detail modal
    setRequestTradeModal({ isOpen: false, item: null });
    setPieceDetailModal({ isOpen: false, item: null });
  };

  // Handle trade request success
  const handleTradeRequestSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessNotification(true);
    setTimeout(() => setShowSuccessNotification(false), 4000);
  };

  // Process offer submission
  const processOffer = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    const { item, offerAmount, expirationDays, expirationHours } = offerModal;
    const offerPrice = parseFloat(offerAmount);
    
    if (!offerPrice || offerPrice <= 0 || isNaN(offerPrice)) {
      showNotification('Please enter a valid offer amount', 'error');
      return;
    }

    if (balance < offerPrice) {
      showNotification(`Insufficient balance. You need ${offerPrice} SOL but only have ${balance.toFixed(4)} SOL`, 'error');
      return;
    }

    // Validate required fields
    if (!item.id || !item.mint_address) {
      showNotification('This piece is not ready for offers yet. Please try again later.', 'error');
      return;
    }

    // Extract the real piece ID (remove 'supabase_' prefix if present)
    let realPieceId = item.id;
    if (typeof item.id === 'string' && item.id.startsWith('supabase_')) {
      realPieceId = parseInt(item.id.replace('supabase_', ''));
    }

    setIsProcessing(true);
    
    try {
      console.log(`📝 Creating offer for ${item.name} of ${offerPrice} SOL`);
      
      // Create offer using the real database function
      const result = await createOffer(wallet, {
        pieceId: realPieceId,
        mintAddress: item.mint_address,
        pieceName: item.name,
        offerPrice: offerPrice,
        message: '',
        expirationDays: expirationDays,
      });
      
      if (!result.success) {
        // Check if it's a duplicate offer error
        if (result.error && result.error.includes('already have an active offer')) {
          showNotification(
            'You already have an active offer on this piece. Visit the Offers page to view or update it.',
            'error'
          );
          setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
          return;
        }
        throw new Error(result.error || 'Failed to create offer');
      }
      
      console.log('✅ Offer created successfully!');
      
      showNotification(
        `Offer of ${offerPrice} SOL submitted successfully! 🎉`,
        'success'
      );
      
      // Close modal
      setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
      
    } catch (error) {
      console.error('❌ Offer creation failed:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Unable to create offer. Please try again.';
      
      // Check if error message contains the duplicate offer info
      if (error.message && error.message.includes('already have an active offer')) {
        errorMessage = 'You already have an active offer on this piece. Visit the Offers page to view or update it.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification(errorMessage, 'error');
      
      // Close modal if it's a duplicate offer error
      if (errorMessage.includes('already have an active offer')) {
        setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Close offer modal
  const closeOfferModal = () => {
    setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7, expirationHours: 0 });
  };

  // Handle piece detail modal
  const openPieceDetailModal = async (activityOrPiece) => {
    console.log('🔍 [ACTIVITY] Opening piece detail modal with data:', activityOrPiece);
    
    // Get piece ID from activity or piece object - try multiple sources
    let pieceId = activityOrPiece.pieceId || 
                  activityOrPiece.piece_id || 
                  activityOrPiece.metadata?.pieceId ||
                  activityOrPiece.metadata?.piece_id ||
                  activityOrPiece.id;
    
    console.log('🔍 [ACTIVITY] Extracted piece ID (raw):', pieceId, 'type:', typeof pieceId);
    
    if (!pieceId) {
      console.error('❌ No piece ID found in:', activityOrPiece);
      return;
    }

    // Strip any prefixes from the piece ID (supabase_, local_, etc)
    const pieceIdString = String(pieceId);
    if (pieceIdString.startsWith('supabase_')) {
      pieceId = pieceIdString.replace('supabase_', '');
      console.log('🔄 [ACTIVITY] Stripped "supabase_" prefix, new ID:', pieceId);
    } else if (pieceIdString.startsWith('local_')) {
      // Local pieces might not be in database
      console.warn('⚠️ Cannot fetch local piece from database:', pieceId);
      const fallbackData = {
        ...activityOrPiece,
        id: activityOrPiece.pieceId || activityOrPiece.id,
        name: activityOrPiece.pieceName || activityOrPiece.piece_name || activityOrPiece.name || 'Unknown Piece',
        thumbnailUrl: activityOrPiece.image || activityOrPiece.thumbnail_url
      };
      setPieceDetailModal({ isOpen: true, item: fallbackData });
      setModalViewMode('thumbnail');
      return;
    }
    
    // Convert to integer if numeric
    if (!isNaN(pieceId)) {
      pieceId = parseInt(pieceId, 10);
    }

    try {
      // Fetch full piece data from database
      console.log('🔄 [ACTIVITY] Fetching piece data from database for ID:', pieceId, 'type:', typeof pieceId);
      const { data: pieceData, error } = await supabase
        .from('pieces')
        .select(`
          *,
          social_links,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            verification_type,
            profile_picture_url,
            banner_url,
            bio,
            social_links,
            total_pieces_created
          )
        `)
        .eq('id', pieceId)
        .single();
      
      console.log('📦 [ACTIVITY] Raw piece data from database:', pieceData);
      console.log('📦 [ACTIVITY] Database error (if any):', error);

      if (error || !pieceData) {
        console.error('❌ Error fetching piece data:', error);
        console.log('📦 [ACTIVITY] Using fallback data from activity object');
        
        // Fallback to using activity data if fetch fails
        const fallbackData = {
          id: pieceId,
          name: activityOrPiece.pieceName || activityOrPiece.piece_name || activityOrPiece.title || 'Unknown Piece',
      description: activityOrPiece.description || 'A premium digital fashion piece with modern styling and high-quality design.',
      price: parseFloat(activityOrPiece.price?.replace(' SOL', '') || '0'),
      category: activityOrPiece.collection || 'Uncategorized',
      color: 'Various',
      tags: [activityOrPiece.collection || 'Digital Fashion'],
          thumbnailUrl: activityOrPiece.image || activityOrPiece.thumbnail_url || '/images/piece-placeholder.png',
          thumbnail_url: activityOrPiece.image || activityOrPiece.thumbnail_url || '/images/piece-placeholder.png',
      quantity: activityOrPiece.quantity || '1',
      originalCreator: {
            username: activityOrPiece.user || activityOrPiece.from || 'Creator',
            wallet: activityOrPiece.from || activityOrPiece.wallet,
        piecesCount: 1
      },
          creator: {
            username: activityOrPiece.user || activityOrPiece.from || 'Creator',
            wallet: activityOrPiece.from || activityOrPiece.wallet
          }
        };
        
        console.log('📦 [ACTIVITY] Fallback data created:', fallbackData);
        
        setPieceDetailModal({ isOpen: true, item: fallbackData });
        setModalViewMode('thumbnail');
        setThreeDStatus('error');
        setThreeDError('No 3D model available');
        return;
      }

      // Merge activity data with fresh piece data (matching Explore.jsx approach)
      const mergedItem = { ...activityOrPiece, ...pieceData };
      
      // Normalize the piece data to ensure consistent field names
      const normalizedItem = normalizePieces([mergedItem])[0];
      
      console.log('✅ [ACTIVITY] Merged piece data:', {
        id: normalizedItem.id,
        name: normalizedItem.name,
        preview_glb_url: normalizedItem.preview_glb_url,
        thumbnail_url: normalizedItem.thumbnail_url,
        thumbnailUrl: normalizedItem.thumbnailUrl,
        preview_bucket: normalizedItem.preview_bucket,
        preview_key: normalizedItem.preview_key,
        creator: normalizedItem.creator,
        originalCreator: normalizedItem.originalCreator
      });
      
      setPieceDetailModal({ isOpen: true, item: normalizedItem });
      
      // Track view (only if not already tracked in this session)
      if (normalizedItem?.id && lastTrackedPiece.current !== String(normalizedItem.id)) {
        const walletAddress = publicKey?.toString() || null;
        const pieceIdString = String(normalizedItem.id);
        const result = await trackPieceView(pieceIdString, walletAddress);
        if (result.success) {
          console.log('✅ [ACTIVITY] View tracked:', result.stats);
          lastTrackedPiece.current = pieceIdString;
        } else {
          console.log('⏭️ [ACTIVITY] View not tracked:', result.error);
          lastTrackedPiece.current = pieceIdString;
        }
      }
      
      // Check if piece belongs to a capsule
      try {
        console.log('🔍 Checking capsule for piece:', pieceId);
        const capsuleInfo = await getCapsuleForPiece(pieceId);
        console.log('🔍 Capsule info result:', capsuleInfo);
        setPieceCapsuleInfo(capsuleInfo);
      } catch (capsuleError) {
        console.error('❌ Error fetching capsule info:', capsuleError);
        setPieceCapsuleInfo(null);
      }
      
      // Check if piece has 3D model and set initial view mode (matching Explore.jsx logic)
      const has3DModel = normalizedItem?.previewUrl && (normalizedItem.previewUrl.includes('.glb') || normalizedItem.previewUrl.includes('.gltf'));
      
      console.log('🔍 [ACTIVITY] 3D Model Detection:', {
        has3DModel,
        previewUrl: normalizedItem?.previewUrl,
        thumbnailUrl: normalizedItem?.thumbnailUrl,
        has3DModel_flag: normalizedItem?.has3DModel
      });
      
      if (has3DModel) {
        setModalViewMode('3d');
        setThreeDStatus('loading');
        setThreeDError(null);
      } else {
        setModalViewMode('thumbnail');
        setThreeDStatus('error');
        setThreeDError('No 3D model available');
      }
    } catch (error) {
      console.error('❌ Exception fetching piece data:', error);
      
      // Fallback to basic data
      const fallbackData = {
        id: pieceId,
        name: activityOrPiece.pieceName || activityOrPiece.piece_name || 'Unknown Piece',
        thumbnailUrl: activityOrPiece.image || activityOrPiece.thumbnail_url,
        description: 'A premium digital fashion piece with modern styling and high-quality design.',
        price: parseFloat(activityOrPiece.price?.replace(' SOL', '') || '0'),
        quantity: '1'
      };
      setPieceDetailModal({ isOpen: true, item: fallbackData });
    }
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
    setModalViewMode('3d');
    setThreeDStatus('loading');
    setThreeDError(null);
    setActivityPanelOpen(false);
    setPieceCapsuleInfo(null);
    // Reset edition state
    setGroupedPieceIds([]);
    setAllEditions([]);
    setSelectedEditionIndex(0);
    setShowEditionSelector(false);
  };

  // Handle view entire capsule button click
  const handleViewCapsule = () => {
    if (pieceCapsuleInfo?.id) {
      navigate(`/capsule/${pieceCapsuleInfo.id}`);
      closePieceDetailModal();
    }
  };

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // Handle holder button click - navigate to holder profile
  const handleHolderClick = (item) => {
    const holderWallet = item?.holder?.wallet || item?.holder_wallet;
    if (holderWallet) {
      navigate(`/creator/${holderWallet}`);
    }
  };

  // Handle capsule navigation
  const handleCapsuleClick = (activity) => {
    // Extract capsule ID from activity data (check multiple sources)
    const capsuleId = activity.capsuleId || activity.collectionId || activity.id;
    if (capsuleId) {
      navigate(`/capsule/${capsuleId}`);
    } else {
      console.error('No capsule ID found in activity:', activity);
    }
  };

  // Refresh activities manually
  const handleRefreshActivities = useCallback(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Bookmark current view
  const bookmarkView = () => {
    const view = {
      id: Date.now(),
      name: `${selectedEventTypes.join(', ')} - All Time`,
      filters: {
        eventTypes: selectedEventTypes,
        timeRange: 'all'
      },
      createdAt: new Date()
    };
    setBookmarkedViews(prev => [...prev, view]);
  };

  // Load bookmarked view
  const loadBookmarkedView = (view) => {
    setSelectedEventTypes(view.filters.eventTypes || [view.filters.eventType] || ['all']);
  };

  // Export activities using context method
  const handleExportActivities = () => {
    exportActivities('csv');
  };

  // Fetch activities on mount and periodically (fallback for real-time)
  useEffect(() => {
    // Initial fetch
    fetchActivities();
    
    // Periodic refresh as fallback (in case real-time misses something)
    // Less frequent since we have real-time updates
    const interval = setInterval(() => {
      console.log('🔄 Periodic activity refresh (fallback)');
      fetchActivities();
    }, 60000); // Every minute
    
    return () => clearInterval(interval);
  }, [fetchActivities]);

  // Filter activities when filters change
  useEffect(() => {
    filterActivities();
  }, [filterActivities]);

  // Intersection Observer ref for infinite scroll sentinel
  const infiniteScrollSentinelRef = useRef(null);
  
  // Set up efficient Intersection Observer for infinite scroll
  useEffect(() => {
    if (!infiniteScrollSentinelRef.current) return;
    
    const options = {
      root: null, // viewport
      rootMargin: '100px', // Start loading 100px before reaching the sentinel
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      
      // Trigger load when sentinel is visible and we're not already loading
      if (entry.isIntersecting && !isLoadingMore && hasMoreActivities) {
        console.log('📊 Infinite scroll triggered via Intersection Observer');
        loadMoreActivities();
      }
    }, options);
    
    observer.observe(infiniteScrollSentinelRef.current);
    
    return () => {
      if (infiniteScrollSentinelRef.current) {
        observer.unobserve(infiniteScrollSentinelRef.current);
      }
    };
  }, [loadMoreActivities, hasMoreActivities, isLoadingMore]);

  // Real-time updates using Supabase subscriptions for ALL activity sources
  useEffect(() => {
    console.log('📡 Setting up real-time subscriptions for all activity tables...');
    
    // Subscribe to multiple tables for complete real-time updates
    const subscription = supabase
      .channel('all_activity_changes')
      // NFT Activity (blockchain events)
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'nft_activity' 
        }, 
        (payload) => {
          console.log('🔔 NEW NFT ACTIVITY DETECTED!', payload.new);
          console.log('🔄 Triggering activity refresh...');
          fetchActivities();
        }
      )
      // Wallet Activity (app events) - but only for meaningful activities, not offer-related
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'wallet_activity' 
        }, 
        (payload) => {
          // Only refresh for non-offer wallet activities (offers are handled separately)
          // Don't trigger refresh for offer stages - only show when sale is complete via nft_activity
          const activityType = payload.new?.activity_type;
          if (activityType !== 'offer_made' && 
              activityType !== 'offer_accepted' &&
              activityType !== 'piece_purchased_via_offer' && 
              activityType !== 'piece_sold_via_offer') {
            console.log('🔔 New wallet activity:', payload.new);
            fetchActivities();
          }
        }
      )
      // Note: Removed offers subscription - active offers are shown via periodic refresh,
      // and accepted offers are shown as sales in nft_activity table
      // P2P Trades
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'p2p_trades',
          filter: 'status=eq.completed'
        }, 
        (payload) => {
          console.log('🔔 Trade completed:', payload.new);
          fetchActivities();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Real-time subscriptions active for nft_activity, wallet_activity, and p2p_trades');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Real-time subscription error');
        }
      });

    return () => {
      console.log('📡 Unsubscribing from real-time updates');
      subscription.unsubscribe();
    };
  }, [fetchActivities]);

  // Update time ago for existing events regularly
  useEffect(() => {
    const updateTimeAgo = () => {
      setFilteredActivities(prev => {
        // Only update if there are activities
        if (prev.length === 0) return prev;
        
        return prev.map(activity => ({
          ...activity,
          timeAgo: formatTimeAgo(activity.timestamp)
        }));
      });
    };

    // Update immediately when activities change
    updateTimeAgo();

    // Update every 10 seconds for recent activities (under 1 hour)
    // This makes "Just now", "30s ago", "5m ago" update smoothly
    const interval = setInterval(updateTimeAgo, 10000);

    return () => clearInterval(interval);
  }, [formatTimeAgo, allActivities.length]); // Re-run when activities change

  // Fetch active listing when piece detail modal opens
  useEffect(() => {
    const fetchActiveListing = async () => {
      if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.mint_address) {
        setActiveListing(null);
        return;
      }

      try {
        setListingLoading(true);
        const listing = await getActiveListing(pieceDetailModal.item.mint_address);
        setActiveListing(listing);
      } catch (error) {
        console.error('Error fetching active listing:', error);
        setActiveListing(null);
      } finally {
        setListingLoading(false);
      }
    };

    fetchActiveListing();
  }, [pieceDetailModal.isOpen, pieceDetailModal.item?.mint_address]);

  // Fetch all editions when piece detail modal opens
  useEffect(() => {
    const fetchAllEditions = async () => {
      if (!pieceDetailModal.isOpen || !pieceDetailModal.item) {
        setGroupedPieceIds([]);
        setAllEditions([]);
        setSelectedEditionIndex(0);
        return;
      }

      const item = pieceDetailModal.item;
      console.log('🎨 [ACTIVITY] Fetching editions for piece:', {
        id: item.id,
        name: item.name,
        master_id: item.master_id,
        creator_id: item.creator_id,
        quantity: item.quantity,
        total_quantity: item.total_quantity
      });

      try {
        let query = supabase
          .from('pieces')
          .select('*');
        
        let queryType = 'unknown';
        
        // Parse quantity values (they might be strings)
        const quantity = parseInt(item.quantity || item.total_quantity || 1);
        
        // Get creator_id from various possible sources
        const creatorId = item.creator_id || item.creatorId || item.creators?.id || item.creator?.id;
        
        // Determine how to fetch editions:
        if (item.master_id) {
          // 1. If piece has master_id, fetch all pieces with same master_id
          console.log('🔍 [ACTIVITY] Fetching all editions with master_id:', item.master_id);
          query = query.eq('master_id', item.master_id);
          queryType = 'master_id';
        } 
        else if (quantity > 1 && item.name && creatorId) {
          // 2. If piece has quantity > 1 but no master_id, fetch by name + creator_id to find all editions
          console.log('🔍 [ACTIVITY] Piece has quantity > 1, fetching editions by name + creator:', { 
            name: item.name, 
            creator_id: creatorId,
            quantity: quantity 
          });
          query = query
            .eq('name', item.name)
            .eq('creator_id', creatorId);
          queryType = 'name+creator_id';
        }
        else {
          // 3. Single piece with no editions
          console.log('ℹ️ [ACTIVITY] Single piece (no master_id, quantity = 1), showing only this piece:', item.id);
          query = query.eq('id', item.id);
          queryType = 'single';
        }
        
        // Order by created_at descending (newest first) to match Explore page
        query = query.order('created_at', { ascending: false });
        
        const { data: editions, error } = await query;
        
        console.log('📊 [ACTIVITY] Query result:', { queryType, count: editions?.length, error });
        
        if (error) {
          console.error('[ACTIVITY] Error fetching editions:', error);
          setGroupedPieceIds([item.id]);
          setAllEditions([item]);
          setSelectedEditionIndex(0);
        } else if (editions && editions.length > 0) {
          const pieceIds = editions.map(p => p.id);
          console.log('✅ [ACTIVITY] Found editions (database order):', pieceIds);
          
          // Normalize editions to ensure consistent field names
          const normalizedEditions = normalizePieces(editions);
          console.log('✅ [ACTIVITY] Normalized editions');
          
          setGroupedPieceIds(pieceIds);
          setAllEditions(normalizedEditions);
          
          // Set selected index to the current piece OR the edition owned by current user
          let targetIndex = normalizedEditions.findIndex(p => p.id === item.id);
          
          // If user owns one of these editions, prioritize showing their owned edition
          if (publicKey) {
            const ownedIndex = normalizedEditions.findIndex(p => 
              p.holder_wallet === publicKey.toString()
            );
            if (ownedIndex >= 0) {
              targetIndex = ownedIndex;
              console.log('📍 [ACTIVITY] User owns edition at index:', ownedIndex);
            }
          }
          
          console.log('📍 [ACTIVITY] Setting selected index:', targetIndex, 'out of', normalizedEditions.length);
          setSelectedEditionIndex(targetIndex >= 0 ? targetIndex : 0);
        } else {
          console.log('⚠️ [ACTIVITY] No editions found, using single piece');
          setGroupedPieceIds([item.id]);
          setAllEditions([item]);
          setSelectedEditionIndex(0);
        }
      } catch (error) {
        console.error('[ACTIVITY] Error in fetchAllEditions:', error);
        setGroupedPieceIds([item.id]);
        setAllEditions([item]);
        setSelectedEditionIndex(0);
      }
    };

    fetchAllEditions();
  }, [pieceDetailModal.isOpen, pieceDetailModal.item?.id, publicKey]);

  // Update active listing when edition changes
  useEffect(() => {
    if (allEditions.length > 0 && pieceDetailModal.isOpen) {
      const currentPiece = allEditions[selectedEditionIndex];
      if (currentPiece?.mint_address) {
        setListingLoading(true);
        getActiveListing(currentPiece.mint_address)
          .then(listing => {
            setActiveListing(listing);
            setListingLoading(false);
          })
          .catch(error => {
            console.error('[ACTIVITY] Error fetching listing:', error);
            setActiveListing(null);
            setListingLoading(false);
          });
      } else {
        setActiveListing(null);
      }
    }
  }, [selectedEditionIndex, allEditions, pieceDetailModal.isOpen]);

  // Real-time subscription for listing and piece changes
  useEffect(() => {
    if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.mint_address) {
      return;
    }
    
    console.log('👁️ Setting up real-time subscriptions for:', pieceDetailModal.item.mint_address);
    
    // Subscribe to listing changes
    const listingSubscription = subscribeToListing(pieceDetailModal.item.mint_address, (payload) => {
      console.log('📡 Listing update received:', payload);
      
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        if (payload.new.status === 'active') {
          setActiveListing(payload.new);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'listed',
              listing_price: payload.new.price_sol
            }
          }));
        } else if (payload.new.status === 'filled') {
          setActiveListing(null);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'sold',
              holder_wallet: payload.new.buyer_wallet
            }
          }));
        } else if (payload.new.status === 'canceled') {
          // Listing canceled - remove buy button
          setActiveListing(null);
          setPieceDetailModal(prev => ({
            ...prev,
            item: {
              ...prev.item,
              listing_status: 'unlisted',
              listing_price: null
            }
          }));
        }
      } else if (payload.eventType === 'DELETE') {
        setActiveListing(null);
      }
    });
    
    // Subscribe to piece changes
    const pieceSubscription = subscribeToPiece(pieceDetailModal.item.mint_address, (payload) => {
      console.log('📡 Piece update received:', payload);
      
      if (payload.eventType === 'UPDATE') {
        setPieceDetailModal(prev => ({
          ...prev,
          item: {
            ...prev.item,
            ...payload.new
          }
        }));
      }
    });
    
    // Cleanup subscriptions
    return () => {
      console.log('🔌 Cleaning up subscriptions...');
      listingSubscription.unsubscribe();
      pieceSubscription.unsubscribe();
    };
  }, [pieceDetailModal.isOpen, pieceDetailModal.item?.mint_address]);

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="max-w-full mx-auto px-4 py-4">
        <div className="mb-2">
          <h1 className="text-3xl font-bold text-white mb-1">Activity Feed</h1>
          <p className="text-gray-400">Real-time Outsera marketplace events</p>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="px-4 py-2 border-b border-[#2a2a2a] bg-black">
        <div className="flex flex-col lg:flex-row gap-4">

        </div>
      </div>

      <div className="flex">
        {/* Left Sidebar Filters */}
        <div className="w-80 border-r border-[#2a2a2a] p-6 space-y-6 bg-[#111111]">
          {/* Event Type Filter */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-white">Event Type</h3>
            </div>
            <div className="space-y-2">
              {Object.entries(eventTypes).map(([key, type]) => {
                const Icon = type.icon;
                const isSelected = selectedEventTypes.includes(key);
                return (
                  <button
                    key={key}
                    onClick={() => toggleEventType(key)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-all duration-200 hover:scale-105 flex items-center gap-2 ${
                      isSelected
                      ? 'text-white bg-[#2a2a2a] shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-[#2a2a2a]'
                  }`}
                >
                    <Icon size={16} className={type.color} />
                    {type.label}
                    {isSelected && selectedEventTypes.length > 1 && (
                      <span className="ml-auto text-xs text-gray-300">✓</span>
                    )}
                </button>
                );
              })}
            </div>
          </div>


          {/* Bookmarked Views */}
          {bookmarkedViews.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-white">Bookmarked Views</h3>
                <Bookmark size={16} className="text-white" />
            </div>
              <div className="space-y-2">
                {bookmarkedViews.map((view) => (
                  <button
                    key={view.id}
                    onClick={() => loadBookmarkedView(view)}
                    className="w-full text-left px-3 py-2 rounded-xl text-sm transition-all duration-200 hover:scale-105 text-gray-400 hover:text-white hover:bg-[#0a0a0a]"
                  >
                    {view.name}
                  </button>
                ))}
          </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-3 md:p-6 bg-[#0a0a0a] min-h-[calc(100vh-120px)]">
          {/* Activity Feed */}
          <div 
            className="md:rounded-2xl border border-[#2a2a2a] overflow-visible md:overflow-hidden shadow-xl"
            style={{ backgroundColor: '#111111' }}
          >
            <div className="bg-[#111111] px-3 md:px-6 py-3 md:py-4 border-b border-[#2a2a2a]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">Activity Feed</h3>
                  <p className="text-gray-400 text-sm">
                    {selectedView} view
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleRefreshActivities}
                    disabled={isLoading}
                    className="p-2 rounded-lg bg-[#0a0a0a] hover:bg-[#2a2a2a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Refresh activities"
                  >
                    <RefreshCw size={16} className={`text-white ${isLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Desktop Table View */}
            <div 
              className="hidden md:block overflow-x-auto max-h-[70vh] overflow-y-auto"
              style={{ 
                contain: 'layout style paint',
                willChange: 'scroll-position'
              }}
            >
              <table className="w-full" style={{ contain: 'layout' }}>
                <thead>
                  <tr className="border-b border-[#2a2a2a]">
                    <th className="text-left p-4 font-semibold text-white">EVENT</th>
                    <th className="text-left p-4 font-semibold text-white">PIECE</th>
                    <th className="text-left p-4 font-semibold text-white">PRICE</th>
                    <th className="text-left p-4 font-semibold text-white">QUANTITY</th>
                    <th className="text-left p-4 font-semibold text-white">FROM</th>
                    <th className="text-left p-4 font-semibold text-white">TO</th>
                    <th className="text-left p-4 font-semibold text-white">TIME</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedActivities.map((activity) => {
                    const eventType = eventTypes[activity.type] || eventTypes.all;
                    const EventIcon = eventType.icon;
                    const isTrade = activity.type === 'PIECE_TRADED';
                    return (
                      <tr 
                        key={activity.id} 
                        className={`border-b border-[#2a2a2a] border-l-4 border-l-transparent transition-all duration-200 hover:bg-[#0a0a0a] hover:border-l-white/50`}
                        style={{ contain: 'layout style paint' }}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0a0a0a] border border-[#2a2a2a]">
                              <EventIcon size={16} className="text-white" />
                            </div>
                            <span className="text-white font-medium">{eventType.label}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {isTrade && activity.tradeImages && activity.tradePieces ? (
                              // Show both pieces for trades with thumbnails and names together
                              <div className="flex items-center gap-3">
                                {/* Initiator Piece */}
                                {activity.tradePieces.initiator && activity.tradePieces.initiator[0] ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const piece = activity.tradePieces.initiator[0];
                                        openPieceDetailModal({
                                          ...piece,
                                          id: piece.id,
                                          name: piece.name,
                                          price: piece.price || piece.listing_price || 0,
                                          imageUrl: activity.tradeImages.initiator,
                                          thumbnail_url: activity.tradeImages.initiator,
                                          preview_glb_url: piece.preview_glb_url,
                                          category: piece.category
                                        });
                                      }}
                                      className="hover:opacity-80 transition-opacity"
                                      title={`View ${activity.tradePieces.initiator[0].name}`}
                                    >
                                      <img 
                                        src={activity.tradeImages.initiator} 
                                        alt={activity.tradePieces.initiator[0].name}
                                        className="w-12 h-12 rounded-xl object-cover border border-[#2a2a2a] hover:border-white/50 transition-colors"
                                        onError={(e) => {
                                          console.error('❌ Trade initiator image failed:', activity.tradeImages.initiator);
                                          e.target.src = '/images/piece-placeholder.png';
                                          e.target.onerror = null;
                                        }}
                                        onLoad={() => {
                                          console.log('✅ Trade initiator image loaded:', activity.tradeImages.initiator);
                                        }}
                                      />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const piece = activity.tradePieces.initiator[0];
                                        openPieceDetailModal({
                                          ...piece,
                                          id: piece.id,
                                          name: piece.name,
                                          price: piece.price || piece.listing_price || 0,
                                          imageUrl: activity.tradeImages.initiator,
                                          thumbnail_url: activity.tradeImages.initiator,
                                          preview_glb_url: piece.preview_glb_url,
                                          category: piece.category
                                        });
                                      }}
                                      className="text-xs text-white hover:text-gray-300 transition-colors text-center max-w-[80px] truncate"
                                    >
                                      {activity.tradePieces.initiator[0].name}
                                    </button>
                                    {activity.tradePieces?.initiator?.length > 1 && (
                                      <span className="text-xs text-gray-400">+{activity.tradePieces.initiator.length - 1} more</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="w-12 h-12 rounded-xl bg-[#2a2a2a] border border-[#2a2a2a]"></div>
                                )}
                                
                                {/* Arrow */}
                                <ArrowLeftRight size={16} className="text-gray-400 flex-shrink-0" />
                                
                                {/* Counterparty Piece */}
                                {activity.tradePieces.counterparty && activity.tradePieces.counterparty[0] ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const piece = activity.tradePieces.counterparty[0];
                                        openPieceDetailModal({
                                          ...piece,
                                          id: piece.id,
                                          name: piece.name,
                                          price: piece.price || piece.listing_price || 0,
                                          imageUrl: activity.tradeImages.counterparty,
                                          thumbnail_url: activity.tradeImages.counterparty,
                                          preview_glb_url: piece.preview_glb_url,
                                          category: piece.category
                                        });
                                      }}
                                      className="hover:opacity-80 transition-opacity"
                                      title={`View ${activity.tradePieces.counterparty[0].name}`}
                                    >
                                      <img 
                                        src={activity.tradeImages.counterparty} 
                                        alt={activity.tradePieces.counterparty[0].name}
                                        className="w-12 h-12 rounded-xl object-cover border border-[#2a2a2a] hover:border-white/50 transition-colors"
                                        onError={(e) => {
                                          console.error('❌ Trade counterparty image failed:', activity.tradeImages.counterparty);
                                          e.target.src = '/images/piece-placeholder.png';
                                          e.target.onerror = null;
                                        }}
                                        onLoad={() => {
                                          console.log('✅ Trade counterparty image loaded:', activity.tradeImages.counterparty);
                                        }}
                                      />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const piece = activity.tradePieces.counterparty[0];
                                        openPieceDetailModal({
                                          ...piece,
                                          id: piece.id,
                                          name: piece.name,
                                          price: piece.price || piece.listing_price || 0,
                                          imageUrl: activity.tradeImages.counterparty,
                                          thumbnail_url: activity.tradeImages.counterparty,
                                          preview_glb_url: piece.preview_glb_url,
                                          category: piece.category
                                        });
                                      }}
                                      className="text-xs text-white hover:text-gray-300 transition-colors text-center max-w-[80px] truncate"
                                    >
                                      {activity.tradePieces.counterparty[0].name}
                                    </button>
                                    {activity.tradePieces?.counterparty?.length > 1 && (
                                      <span className="text-xs text-gray-400">+{activity.tradePieces.counterparty.length - 1} more</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="w-12 h-12 rounded-xl bg-[#2a2a2a] border border-[#2a2a2a]"></div>
                                )}
                              </div>
                            ) : activity.image ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (activity.type === 'COLLECTION_CREATED') {
                                    handleCapsuleClick(activity);
                                  } else {
                                    openPieceDetailModal(activity);
                                  }
                                }}
                                className="hover:opacity-80 transition-opacity"
                                title={`View ${activity.pieceName || activity.title}`}
                              >
                                <img 
                                  src={activity.image} 
                                  alt={activity.pieceName || activity.title}
                                  className="w-12 h-12 rounded-xl object-cover border border-[#2a2a2a] hover:border-white/50 transition-colors"
                                  onError={(e) => {
                                    console.error('❌ Image failed to load:', activity.image, 'for activity:', activity.pieceName);
                                    e.target.src = '/images/piece-placeholder.png';
                                    e.target.onerror = null;
                                  }}
                                  onLoad={() => {
                                    console.log('✅ Image loaded successfully:', activity.image);
                                  }}
                                />
                              </button>
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-[#2a2a2a] border border-[#2a2a2a] flex items-center justify-center">
                                <ImageIcon size={20} className="text-gray-600" />
                              </div>
                            )}
                            {!isTrade && (
                              <div>
                                {activity.type === 'COLLECTION_CREATED' ? (
                                <button
                                  onClick={() => handleCapsuleClick(activity)}
                                  className="font-semibold text-white hover:text-gray-300 transition-colors text-left"
                                >
                                  {(() => {
                                    const title = activity.pieceName || activity.title;
                                    // Extract capsule name from 'Capsule "name" created' format
                                    const match = title.match(/Capsule "(.+)" created/);
                                    return match ? match[1] : title;
                                  })()}
                                </button>
                                ) : (
                                  <button
                                    onClick={() => openPieceDetailModal(activity)}
                                    className="font-semibold text-white hover:text-gray-300 transition-colors text-left"
                                  >
                                    {activity.pieceName || activity.title}
                                  </button>
                                )}
                                {activity.type !== 'PIECE_TRADED' && activity.type !== 'COLLECTION_CREATED' && (
                                  <p className="text-gray-400 text-sm">{activity.collection || activity.description}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          {activity.price && (
                            <div>
                              <span className="text-white font-semibold">{activity.price}</span>
                              {activity.usdPrice && (
                                <p className="text-gray-400 text-sm">{activity.usdPrice}</p>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          {(activity.quantity || (activity.type === 'COLLECTION_CREATED' && activity.pieceCount)) && (
                            <span className="px-2 py-1 rounded-lg text-xs font-medium bg-white/20 text-white border border-white/30">
                              {activity.type === 'COLLECTION_CREATED' 
                                ? `${activity.pieceCount} piece${activity.pieceCount !== 1 ? 's' : ''}`
                                : activity.quantity}
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          {activity.from && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400 font-mono text-sm">{activity.from.slice(0, 8)}...</span>
                              <button
                                onClick={() => copyToClipboard(activity.from, 'from')}
                                className="p-1 rounded hover:bg-[#0a0a0a] transition-colors"
                              >
                                {copiedAddress === 'from' ? (
                                  <CheckCircle size={12} className="text-green-400" />
                                ) : (
                                  <Copy size={12} className="text-gray-400" />
                                )}
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          {activity.to && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400 font-mono text-sm">{activity.to.slice(0, 8)}...</span>
                              <button
                                onClick={() => copyToClipboard(activity.to, 'to')}
                                className="p-1 rounded hover:bg-[#0a0a0a] transition-colors"
                              >
                                {copiedAddress === 'to' ? (
                                  <CheckCircle size={12} className="text-green-400" />
                                ) : (
                                  <Copy size={12} className="text-gray-400" />
                                )}
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-gray-400">{activity.timeAgo}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden max-h-[70vh] overflow-y-auto">
              {displayedActivities.map((activity) => {
                const eventType = eventTypes[activity.type] || eventTypes.all;
                const EventIcon = eventType.icon;
                const isTrade = activity.type === 'PIECE_TRADED';
                return (
                  <div 
                    key={activity.id} 
                    className="border-b border-[#2a2a2a] hover:bg-[#0a0a0a] transition-all duration-200 overflow-x-auto scrollbar-hide"
                    style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
                  >
                    {/* Horizontal scrollable content */}
                    <div className="flex items-center gap-6 p-4" style={{ minWidth: '1480px' }}>
                      {/* Event Type */}
                      <div className="flex items-center gap-2 flex-shrink-0" style={{ width: '150px' }}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0a0a0a] border border-[#2a2a2a]">
                          <EventIcon size={14} className="text-white" />
                        </div>
                        <span className="text-white font-medium text-xs whitespace-nowrap">{eventType.label}</span>
                      </div>

                      {/* Piece Info */}
                      <div className="flex-shrink-0" style={{ width: '220px' }}>
                        {isTrade && activity.tradeImages && activity.tradePieces ? (
                          // Trade: Show both pieces
                          <div className="flex items-center gap-2">
                            {/* Initiator Piece */}
                            {activity.tradePieces.initiator && activity.tradePieces.initiator[0] ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const piece = activity.tradePieces.initiator[0];
                                  openPieceDetailModal({
                                    ...piece,
                                    id: piece.id,
                                    name: piece.name,
                                    price: piece.price || piece.listing_price || 0,
                                    imageUrl: activity.tradeImages.initiator,
                                    thumbnail_url: activity.tradeImages.initiator,
                                    preview_glb_url: piece.preview_glb_url,
                                    category: piece.category
                                  });
                                }}
                                className="flex items-center gap-2"
                              >
                                <img 
                                  src={activity.tradeImages.initiator} 
                                  alt={activity.tradePieces.initiator[0].name}
                                  className="w-10 h-10 rounded-lg object-cover border border-[#2a2a2a] flex-shrink-0 bg-[#1a1a1a]"
                                  style={{ display: 'block', minWidth: '40px', minHeight: '40px' }}
                                  onError={(e) => {
                                    console.log('Image failed to load:', e.target.src);
                                    e.target.style.backgroundColor = '#2a2a2a';
                                    e.target.onerror = null;
                                  }}
                                />
                                <span className="text-xs text-white max-w-[60px] truncate">
                                  {activity.tradePieces.initiator[0].name}
                                </span>
                              </button>
                            ) : null}
                            
                            <ArrowLeftRight size={12} className="text-gray-400 flex-shrink-0" />
                            
                            {/* Counterparty Piece */}
                            {activity.tradePieces.counterparty && activity.tradePieces.counterparty[0] ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const piece = activity.tradePieces.counterparty[0];
                                  openPieceDetailModal({
                                    ...piece,
                                    id: piece.id,
                                    name: piece.name,
                                    price: piece.price || piece.listing_price || 0,
                                    imageUrl: activity.tradeImages.counterparty,
                                    thumbnail_url: activity.tradeImages.counterparty,
                                    preview_glb_url: piece.preview_glb_url,
                                    category: piece.category
                                  });
                                }}
                                className="flex items-center gap-2"
                              >
                                <img 
                                  src={activity.tradeImages.counterparty} 
                                  alt={activity.tradePieces.counterparty[0].name}
                                  className="w-10 h-10 rounded-lg object-cover border border-[#2a2a2a] flex-shrink-0 bg-[#1a1a1a]"
                                  style={{ display: 'block', minWidth: '40px', minHeight: '40px' }}
                                  onError={(e) => {
                                    console.log('Image failed to load:', e.target.src);
                                    e.target.style.backgroundColor = '#2a2a2a';
                                    e.target.onerror = null;
                                  }}
                                />
                                <span className="text-xs text-white max-w-[60px] truncate">
                                  {activity.tradePieces.counterparty[0].name}
                                </span>
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          // Regular piece
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (activity.type === 'COLLECTION_CREATED') {
                                handleCapsuleClick(activity);
                              } else if (activity.pieceId) {
                                openPieceDetailModal({
                                  id: activity.pieceId,
                                  name: activity.pieceName,
                                  price: activity.price || 0,
                                  imageUrl: activity.image || activity.thumbnail,
                                  thumbnail_url: activity.image || activity.thumbnail,
                                  preview_glb_url: activity.preview_glb_url,
                                  category: activity.category
                                });
                              }
                            }}
                            className="flex items-center gap-2"
                          >
                            <img 
                              src={activity.image || activity.thumbnail} 
                              alt={activity.pieceName}
                              className="w-10 h-10 rounded-lg object-cover border border-[#2a2a2a] flex-shrink-0 bg-[#1a1a1a]"
                              style={{ display: 'block', minWidth: '40px', minHeight: '40px' }}
                              onError={(e) => {
                                console.log('Image failed to load:', e.target.src);
                                e.target.style.backgroundColor = '#2a2a2a';
                                e.target.onerror = null;
                              }}
                            />
                            <div className="text-left">
                              <div className="text-white font-medium text-xs max-w-[140px] truncate">{activity.pieceName}</div>
                              {activity.category && (
                                <div className="text-[10px] text-gray-400">{getCategoryDisplayName(activity.category)}</div>
                              )}
                            </div>
                          </button>
                        )}
                      </div>

                      {/* Price */}
                      <div className="flex-shrink-0" style={{ width: '160px' }}>
                        {activity.price && !isTrade && typeof activity.price === 'number' && (
                          <>
                            <div className="text-gray-400 text-[10px] mb-1 whitespace-nowrap">PRICE</div>
                            <div className="flex items-center gap-1">
                              <span className="text-white font-medium text-xs whitespace-nowrap">{formatSOLPrice(activity.price, { includeCurrency: false })}</span>
                              <span className="text-gray-400 text-xs">SOL</span>
                            </div>
                            {solPrice && (
                              <div className="text-gray-500 text-[10px] mt-0.5 whitespace-nowrap">
                                {formatUSDPrice(activity.price, solPrice)}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Quantity */}
                      <div className="flex-shrink-0" style={{ width: '120px' }}>
                        {activity.quantity && (
                          <>
                            <div className="text-gray-400 text-[10px] mb-1 whitespace-nowrap">QUANTITY</div>
                            <div className="text-white font-medium text-xs whitespace-nowrap">{activity.quantity}</div>
                          </>
                        )}
                      </div>

                      {/* From */}
                      <div className="flex-shrink-0" style={{ width: '140px' }}>
                        {activity.from && (
                          <>
                            <div className="text-gray-400 text-[10px] mb-1 whitespace-nowrap">FROM</div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (activity.fromWallet) {
                                    navigate(`/creator/${activity.fromWallet}`);
                                  }
                                }}
                                className="text-white hover:text-gray-300 transition-colors text-left flex items-center gap-1"
                              >
                                <span className="text-xs whitespace-nowrap">{truncateAddress(activity.from)}</span>
                                {activity.fromVerified && (
                                  <VerifiedBadge size="xs" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyAddress(activity.from);
                                }}
                                className="p-0.5 hover:bg-white/10 rounded transition-colors flex-shrink-0"
                                title="Copy address"
                              >
                                {copiedAddress === activity.from ? (
                                  <CheckCircle size={12} className="text-green-500" />
                                ) : (
                                  <Copy size={12} className="text-gray-400" />
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>

                      {/* To */}
                      <div className="flex-shrink-0" style={{ width: '140px' }}>
                        {activity.to && (
                          <>
                            <div className="text-gray-400 text-[10px] mb-1 whitespace-nowrap">TO</div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (activity.toWallet) {
                                    navigate(`/creator/${activity.toWallet}`);
                                  }
                                }}
                                className="text-white hover:text-gray-300 transition-colors text-left flex items-center gap-1"
                              >
                                <span className="text-xs whitespace-nowrap">{truncateAddress(activity.to)}</span>
                                {activity.toVerified && (
                                  <VerifiedBadge size="xs" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyAddress(activity.to);
                                }}
                                className="p-0.5 hover:bg-white/10 rounded transition-colors flex-shrink-0"
                                title="Copy address"
                              >
                                {copiedAddress === activity.to ? (
                                  <CheckCircle size={12} className="text-green-500" />
                                ) : (
                                  <Copy size={12} className="text-gray-400" />
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Time */}
                      <div className="flex-shrink-0" style={{ width: '120px' }}>
                        <div className="text-gray-400 text-[10px] mb-1 whitespace-nowrap">TIME</div>
                        <div className="text-gray-400 text-xs whitespace-nowrap">{activity.timeAgo}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Loading indicator */}
            {isLoading && (
              <div className="p-8 text-center">
                <div className="inline-flex items-center gap-2 text-white">
                  <RefreshCw size={16} className="animate-spin" />
                  Loading activities...
                </div>
              </div>
            )}

            {/* Empty state */}
            {displayedActivities.length === 0 && memoizedFilteredActivities.length === 0 ? (
              <div className="p-12 text-center">
                <ActivityIcon size={48} className="text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No activities found</h3>
                <p className="text-gray-400">Try adjusting your filters or search terms</p>
              </div>
            ) : (
              <>
                {/* Intersection Observer sentinel for infinite scroll */}
                {hasMoreActivities && (
                  <div 
                    ref={infiniteScrollSentinelRef}
                    className="h-4 w-full"
                    style={{ visibility: 'hidden' }}
                  />
                )}
                
                {/* Loading indicator */}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-4 border-t border-[#2a2a2a]">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span className="text-gray-400 text-sm">Loading more activities...</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>

      {/* Piece Detail Modal */}
      {pieceDetailModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closePieceDetailModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
               {/* Left side - Thumbnail/3D Model */}
               <div className="w-1/2 p-6 overflow-y-auto">
                 {/* 3D Model Toggle Buttons - Always show */}
                 <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                      <button
                     onClick={() => {
                       console.log('🔘 Switching to 3D view');
                       setModalViewMode('3d');
                     }}
                     className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                       modalViewMode === '3d'
                         ? 'text-white bg-[#2a2a2a] shadow-lg' 
                         : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                     }`}
                   >
                        3D Model
                      </button>
                      <button
                     onClick={() => {
                       console.log('🔘 Switching to thumbnail view');
                       setModalViewMode('thumbnail');
                     }}
                     className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                       modalViewMode === 'thumbnail'
                         ? 'text-white bg-[#2a2a2a] shadow-lg' 
                         : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                     }`}
                   >
                        Thumbnail
                      </button>
                    </div>
                
                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square bg-[#1a1a1a]">
                  {(() => {
                    const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                    
                    console.log('🖼️ [ACTIVITY] Rendering view mode:', modalViewMode);
                    console.log('🖼️ [ACTIVITY] Normalized piece for display:', {
                      previewUrl: normalizedPiece?.previewUrl,
                      thumbnailUrl: normalizedPiece?.thumbnailUrl,
                      has3DModel: normalizedPiece?.has3DModel
                    });
                    
                    if (modalViewMode === '3d') {
                      // Try to get bucket/key info from the piece data
                      const modelBucket = pieceDetailModal.item?.preview_bucket || pieceDetailModal.item?.model_bucket;
                      const modelKey = pieceDetailModal.item?.preview_key || pieceDetailModal.item?.model_key;
                      
                      // Fallback to legacy URL if bucket/key not available
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      if (modelBucket && modelKey) {
                        console.log('🎮 [ACTIVITY] Rendering ModelViewer with bucket/key:', { modelBucket, modelKey });
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ [ACTIVITY] Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ [ACTIVITY] Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else if (fallbackSrc) {
                        console.log('🎮 [ACTIVITY] Rendering ModelViewer with URL fallback:', fallbackSrc);
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            src={fallbackSrc}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                            onLoad={() => {
                              console.log('✅ [ACTIVITY] Modal: 3D model loaded successfully');
                              setThreeDStatus('loaded');
                            }}
                            onError={(error) => {
                              console.error('❌ [ACTIVITY] Modal: 3D model error:', error);
                              setThreeDStatus('error');
                              setThreeDError(error.message || 'Failed to load 3D model');
                            }}
                          />
                        );
                      } else {
                        console.warn('⚠️ [ACTIVITY] No 3D model source found:', { 
                          modelBucket, 
                          modelKey, 
                          fallbackSrc,
                          preview_glb_url: pieceDetailModal.item?.preview_glb_url,
                          model_url: pieceDetailModal.item?.model_url 
                        });
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-[#0a0a0a] rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image - use normalized piece data to ensure proper field names
                      return normalizedPiece?.thumbnailUrl ? (
                        <img 
                          src={normalizedPiece.thumbnailUrl} 
                          alt={normalizedPiece.name || pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', normalizedPiece.name, 'URL:', normalizedPiece.thumbnailUrl);
                            e.target.src = '/images/piece-placeholder.png';
                            e.target.onerror = null;
                          }}
                        />
                      ) : pieceDetailModal.item?.thumbnail?.dataUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnail.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            e.target.src = '/images/piece-placeholder.png';
                            e.target.onerror = null;
                          }}
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover rounded-xl"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Creator and Holder Section */}
                <div className="space-y-4 mb-6">
                  {/* Original Creator */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                <button 
                  onClick={() => handleAccountClick(pieceDetailModal.item)}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                    {(pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url) ? (
                      <img
                        src={pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url}
                        alt={pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator'}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {(pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator').charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                      {pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Original Creator'}
                    </div>
                          {(pieceDetailModal.item?.originalCreator?.verified || pieceDetailModal.item?.creator?.verified) && (
                            <VerifiedBadge 
                              size="sm"
                              verificationType={pieceDetailModal.item?.originalCreator?.verificationType || pieceDetailModal.item?.creator?.verificationType || 'none'}
                            />
                          )}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                      {pieceDetailModal.item?.originalCreator?.piecesCount || pieceDetailModal.item?.creator?.piecesCount || '0'} pieces created
                    </div>
                  </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </div>
                </button>

                {/* Creator Royalty - Outside button */}
                {pieceDetailModal.item?.royalty_bps !== undefined && pieceDetailModal.item?.royalty_bps !== null && (
                  <div className="mt-3 px-1">
                    <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                      {(pieceDetailModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                    </span>
                  </div>
                )}

                {/* Social Links */}
                {(() => {
                  // Prioritize capsule social links if piece belongs to a capsule, otherwise use piece social links
                  const socialLinks = pieceCapsuleInfo 
                    ? {
                        website: pieceCapsuleInfo.website,
                        twitter: pieceCapsuleInfo.twitter,
                        instagram: pieceCapsuleInfo.instagram,
                        discord: pieceCapsuleInfo.discord
                      }
                    : (pieceDetailModal.item?.social_links || {});
                  const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                  
                  // DEBUG: Log social links data
                  console.log('🔍 [ACTIVITY] Social Links Debug:', {
                    pieceId: pieceDetailModal.item?.id,
                    pieceName: pieceDetailModal.item?.name,
                    pieceCapsuleInfo: pieceCapsuleInfo,
                    usingCapsuleLinks: !!pieceCapsuleInfo,
                    social_links: socialLinks,
                    hasSocialLinks
                  });
                  
                  if (!hasSocialLinks) return null;
                  
                  return (
                        <div className="flex items-center gap-2 mt-3 px-1">
                      {socialLinks.website && (
                        <a
                          href={socialLinks.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Website"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.twitter && (
                        <a
                          href={socialLinks.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="X (Twitter)"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.instagram && (
                        <a
                          href={socialLinks.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Instagram"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.discord && (
                        <a
                          href={socialLinks.discord}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Discord"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  );
                })()}
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pieceDetailModal.item?.name}
                      </h2>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(pieceDetailModal.item.id);
                        }}
                        className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        title={isFavorited(pieceDetailModal.item.id) ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Heart 
                          size={20} 
                          className={`transition-all duration-300 ${
                            isFavorited(pieceDetailModal.item.id)
                              ? 'text-red-500 fill-red-500'
                              : 'text-gray-400 hover:text-red-500'
                          }`} 
                        />
                      </button>
                    </div>
                    {/* Custom Tags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {pieceDetailModal.item?.tags && pieceDetailModal.item.tags.length > 0 ? (
                        pieceDetailModal.item.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                    className={`px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                      activityPanelOpen 
                        ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                        : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                    }`}
                  >
                    Activity
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Edition Selector - Only show if multiple editions */}
                  {(() => {
                    const hasMultipleEditions = allEditions.length > 1;
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    
                    return hasMultipleEditions && (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <div className="mb-2">
                          <span className="text-gray-400 text-sm font-semibold">Select Piece</span>
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowEditionSelector(!showEditionSelector);
                          }}
                          className="w-full flex items-center justify-between p-3 bg-[#111111] hover:bg-[#1a1a1a] rounded-lg transition-all border border-[#2a2a2a]"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm font-medium">Piece #{selectedEditionIndex + 1}</span>
                            <span className="text-xs text-gray-500 font-mono">
                              {currentPiece.mint_address?.slice(0, 8)}...
                            </span>
                          </div>
                          {showEditionSelector ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                        </button>

                        {showEditionSelector && (
                          <div className="mt-2 max-h-40 overflow-y-auto bg-[#111111] rounded-lg border border-[#2a2a2a]">
                            {allEditions.map((piece, index) => (
                              <button
                                key={piece.id || index}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEditionIndex(index);
                                  setShowEditionSelector(false);
                                }}
                                className={`w-full flex items-center justify-between p-2.5 hover:bg-[#1a1a1a] transition-all text-left ${
                                  index === selectedEditionIndex ? 'bg-[#1a1a1a]' : ''
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-white text-sm">Piece #{index + 1}</span>
                                  <span className="text-xs text-gray-500 font-mono">
                                    {piece.mint_address?.slice(0, 6)}...{piece.mint_address?.slice(-4)}
                                  </span>
                                </div>
                                {index === selectedEditionIndex && (
                                  <span className="text-green-400 text-xs font-medium">✓</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Category and Color */}
                  {(() => {
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    return (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                            <span className="text-gray-300 text-sm font-medium">
                              {getCategoryDisplayName(currentPiece?.category) || 'Uncategorized'}
                            </span>
                          </div>
                          {currentPiece?.color && (
                            <div className="text-gray-300 text-sm">
                              <span className="text-gray-500">Color: </span>
                              <span className="capitalize font-medium">{currentPiece.color}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Description */}
                  {(() => {
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    return (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {currentPiece?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                        </p>
                      </div>
                    );
                  })()}

                  {/* Price */}
                  {(() => {
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    return (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Price</span>
                            <div className="text-green-400 font-bold text-xl">
                              {(() => {
                                const isOwnedByCurrentUser = publicKey && currentPiece?.holder_wallet === publicKey.toString();
                                const isListed = currentPiece?.listing_status === 'listed';
                                
                                let displayPrice;
                                // If user owns this piece
                                if (isOwnedByCurrentUser) {
                                  // If they've listed it, show THEIR listing price
                                  if (isListed && currentPiece?.listing_price) {
                                    displayPrice = currentPiece.listing_price;
                                  }
                                  // If not listed, show purchase price
                                  else if (!isListed && currentPiece?.last_sale_price_lamports) {
                                    displayPrice = currentPiece.last_sale_price_lamports / 1_000_000_000;
                                  }
                                  else {
                                    displayPrice = currentPiece?.price || 0;
                                  }
                                } else {
                                  // For pieces not owned by user, show lowest available price
                                  displayPrice = currentPiece?.lowestPrice ?? currentPiece?.listing_price ?? currentPiece?.price;
                                }
                                
                                // Format price to remove trailing zeros
                                const formatted = typeof displayPrice === 'number' 
                                  ? displayPrice.toFixed(4).replace(/\.?0+$/, '')
                                  : displayPrice;
                                return formatted;
                              })()} SOL
                            </div>
                          </div>
                          <div className="text-right text-gray-500 text-sm">
                            ≈ ${(() => {
                              const isOwnedByCurrentUser = publicKey && currentPiece?.holder_wallet === publicKey.toString();
                              const isListed = currentPiece?.listing_status === 'listed';
                              
                              let displayPrice;
                              if (isOwnedByCurrentUser) {
                                if (isListed && currentPiece?.listing_price) {
                                  displayPrice = currentPiece.listing_price;
                                } else if (!isListed && currentPiece?.last_sale_price_lamports) {
                                  displayPrice = currentPiece.last_sale_price_lamports / 1_000_000_000;
                                } else {
                                  displayPrice = currentPiece?.price || 0;
                                }
                              } else {
                                displayPrice = currentPiece?.lowestPrice ?? currentPiece?.listing_price ?? currentPiece?.price;
                              }
                              return ((displayPrice || 0) * solPrice).toFixed(2);
                            })()} USD
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Scarcity */}
                  {(() => {
                    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                    return (
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                        <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                        <div className="text-white font-semibold text-lg">
                          {allEditions.length > 1 ? allEditions.length : (currentPiece?.quantity || 1)}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Dropped Date */}
                  <div className="text-gray-500 text-sm pt-2">
                    Dropped {pieceDetailModal.item?.created_at ? new Date(pieceDetailModal.item.created_at).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : pieceDetailModal.item?.createdAt ? new Date(pieceDetailModal.item.createdAt).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Recently'}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 pb-2">
                    {/* PRIMARY & SECONDARY MARKETPLACE BUTTONS */}
                    {(() => {
                      const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                      const isOwner = isConnected && publicKey && 
                        currentPiece?.holder_wallet === publicKey.toString();
                      
                      const isSeller = isConnected && publicKey && 
                        activeListing?.seller_wallet === publicKey.toString();
                      
                      // Check if piece is owned by the original creator (primary sale)
                      const creatorWallet = currentPiece?.creator_wallet || 
                                           currentPiece?.creators?.wallet ||
                                           currentPiece?.originalCreator?.wallet;
                      const isOwnedByCreator = currentPiece?.holder_wallet === creatorWallet;
                      
                      const listEligibility = canListPiece(wallet, currentPiece);
                      const purchaseEligibility = canPurchaseListing(wallet, activeListing, currentPiece);
                      
                      return (
                        <>
                          {/* OWNERSHIP BADGE: Show when user owns the piece */}
                          {isOwner && (
                            <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-xl text-center border border-green-600/30 mb-2">
                              <span className="text-sm font-medium">✓ You own this piece</span>
                            </div>
                          )}
                          
                          {/* PRIMARY SALE: Buy directly from creator (no listing needed) */}
                          {!isOwner && isOwnedByCreator && 
                           currentPiece?.mint_address && 
                           !activeListing &&
                           currentPiece?.listing_status !== 'sold' && (
                            <button
                              onClick={() => handleBuyNow(currentPiece)}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25"
                            >
                              Buy Now - {(currentPiece?.listing_price || currentPiece?.price || 0).toFixed(4)} SOL
                            </button>
                          )}
                          
                          {/* SECONDARY SALE: Buyer can purchase listed piece */}
                          {!isOwner && activeListing && activeListing.status === 'active' && 
                           currentPiece?.listing_status === 'listed' && (
                            <button
                              onClick={() => handlePurchaseListing(currentPiece)}
                              disabled={listingLoading || !purchaseEligibility.canPurchase}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                              title={!purchaseEligibility.canPurchase ? purchaseEligibility.reason : ''}
                            >
                              {listingLoading ? 'Processing...' : `Buy Now - ${activeListing.price_sol} SOL`}
                            </button>
                          )}
                          
                          {/* FALLBACK: Show buy button for listed pieces even if activeListing hasn't loaded yet */}
                          {!isOwner && !isOwnedByCreator && !activeListing &&
                           currentPiece?.listing_status === 'listed' &&
                           currentPiece?.listing_price && (
                            <button
                              onClick={() => handleBuyNow(currentPiece)}
                              disabled={listingLoading}
                              className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                            >
                              {listingLoading ? 'Loading...' : `Buy Now - ${(currentPiece?.listing_price || 0).toFixed(4)} SOL`}
                            </button>
                          )}
                          
                          {/* Show sold status only if viewer is NOT the current owner */}
                          {currentPiece?.listing_status === 'sold' && !isOwner && (
                            <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                              ✅ Sold
                            </div>
                          )}
                          
                          {/* Show unlisted status */}
                          {currentPiece?.listing_status === 'unlisted' && !isOwner && !isOwnedByCreator && (
                            <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                              Not for Sale
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Make Offer and Request Trade Buttons */}
                    {(() => {
                      const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : pieceDetailModal.item;
                      const isOwner = isConnected && publicKey && 
                        currentPiece?.holder_wallet === publicKey.toString();
                      
                      // Don't show any action buttons if user owns the piece
                      if (isOwner) {
                        return null;
                      }
                      
                      return (
                        <div className="flex gap-3">
                          <button 
                            onClick={() => handleMakeOffer(currentPiece)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                          >
                            Make Offer
                          </button>
                          <button 
                            onClick={() => handleRequestTrade(currentPiece)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a] flex items-center justify-center gap-2"
                          >
                            <ArrowLeftRight size={16} />
                            Request Trade
                          </button>
                        </div>
                      );
                    })()}

                    {/* View Entire Capsule Button - Only show if piece belongs to a capsule */}
                    {pieceCapsuleInfo && (
                      <div className="pt-3">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCapsule();
                          }}
                          className="w-full px-4 py-2.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#3a3a3a] hover:border-[#4a4a4a] text-gray-300 hover:text-white rounded-lg transition-all duration-200 font-medium text-sm flex items-center justify-center gap-2"
                        >
                          <Package size={14} />
                          View entire capsule
                        </button>
                      </div>
                    )}
                  </div>
                      </div>
                      </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : 'translateX(-100%)',
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                    </button>
                  </div>
                <div className="flex-1 overflow-y-auto">
                  <PieceActivityAndHolders
                    pieceId={pieceDetailModal.item?.id}
                    pieceName={pieceDetailModal.item?.name}
                    basePrice={parseFloat(pieceDetailModal.item?.price) || 0}
                    solPrice={solPrice || 150}
                    totalQuantity={groupedPieceIds.length > 0 ? groupedPieceIds.length : (pieceDetailModal.item?.total_quantity || pieceDetailModal.item?.quantity || 1)}
                    groupedPieceIds={groupedPieceIds.length > 0 ? groupedPieceIds : null}
                  />
                </div>
              </div>
          </div>
          </div>
        </div>
      )}

      {/* Buy Modal */}
      {buyModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Purchase Item</h3>
                <p className="text-gray-400 text-sm">Complete your purchase</p>
              </div>
            </div>

            {/* Item Name */}
            <h4 className="text-white font-medium text-lg mb-4">{buyModal.item?.name}</h4>
            
            {(() => {
              const itemPrice = parseFloat(buyModal.item?.listing_price || buyModal.item?.price || '0');
              if (!itemPrice || itemPrice <= 0) {
                return (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6">
                    <p className="text-red-400 text-sm">
                      ⚠️ This piece has no price set. Cannot purchase.
                    </p>
                  </div>
                );
              }
              return (
                <>
                  {/* Price Breakdown */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-blue-400">Price</span>
                      <div className="text-right">
                        <div className="text-green-400 font-bold text-lg">{itemPrice.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-sm">
                          ≈ ${(itemPrice * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-400">Sale Fee (2.5%)</span>
                      <div className="text-right">
                        <div className="text-orange-400 font-medium">
                          {(itemPrice * 0.025).toFixed(4)} SOL
                        </div>
                        <div className="text-gray-500 text-sm">
                          ≈ ${((itemPrice * 0.025) * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-gray-400">Seller Receives</span>
                      <div className="text-right">
                        <div className="text-white font-medium">
                          {(itemPrice * 0.975).toFixed(4)} SOL
                        </div>
                        <div className="text-gray-500 text-sm">
                          ≈ ${((itemPrice * 0.975) * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Wallet Information */}
                  <div className="mb-6">
                    <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Connected Wallet</span>
                        <div className="flex items-center gap-2">
                          <CheckCircle size={16} className="text-green-400" />
                          <span className="text-green-400 text-sm font-medium">
                            {isConnected ? 'Connected' : 'Not Connected'}
                          </span>
                        </div>
                      </div>
                      {isConnected && publicKey && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Address</span>
                            <span className="text-white text-sm font-mono">
                              {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-blue-400 text-sm">Balance</span>
                            <div className="text-right">
                              <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                              <div className="text-gray-500 text-xs">
                                ≈ ${(balance * solPrice).toFixed(2)} USD
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Info Message */}
                  <div className="text-gray-300 text-sm mb-6">
                    You are about to purchase this digital fashion piece. The transaction will be processed on the Solana blockchain.
                  </div>
                </>
              );
            })()}
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={closeBuyModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processPurchase}
                disabled={isProcessing || !isConnected || balance < parseFloat(buyModal.item?.price || '0') || !canWalletTransact(wallet)}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                title={!canWalletTransact(wallet) ? 'Please use Phantom or Solflare wallet' : ''}
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet size={16} />
                    {!canWalletTransact(wallet) ? 'Wallet Not Supported' : 'Confirm Purchase'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offer Modal */}
      {offerModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Make an Offer</h3>
                <p className="text-gray-400 text-sm">Submit your offer for this piece</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{offerModal.item?.name}</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">List Price</span>
                  <div className="text-right">
                    <div className="text-white font-bold text-lg">{offerModal.item?.price} SOL</div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(parseFloat(offerModal.item?.price || '0') * solPrice).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>

              {/* Offer Amount Input */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-3">Your Offer Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offerModal.offerAmount}
                    onChange={(e) => setOfferModal(prev => ({ ...prev, offerAmount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                    SOL
                  </div>
                </div>
                {offerModal.offerAmount && !isNaN(parseFloat(offerModal.offerAmount)) && (
                  <div className="mt-2 text-right text-gray-500 text-sm">
                    ≈ ${(parseFloat(offerModal.offerAmount) * solPrice).toFixed(2)} USD
                  </div>
                )}
              </div>

              {/* Offer Expiration Time */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-3">Offer Expiration</label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-gray-400 text-sm mb-2">Days</label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={offerModal.expirationDays === 0 ? '' : offerModal.expirationDays}
                      onChange={(e) => setOfferModal(prev => ({ ...prev, expirationDays: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                      className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-gray-400 text-sm mb-2">Hours</label>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={offerModal.expirationHours === 0 ? '' : offerModal.expirationHours}
                      onChange={(e) => setOfferModal(prev => ({ ...prev, expirationHours: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                      className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="mt-2 text-gray-500 text-sm">
                  Offer will expire in {offerModal.expirationDays} day{offerModal.expirationDays !== 1 ? 's' : ''} and {offerModal.expirationHours} hour{offerModal.expirationHours !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                Your offer will be locked in escrow until the seller accepts or rejects it, or until it expires. You can cancel your offer at any time before expiration.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeOfferModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processOffer}
                disabled={isProcessing || !isConnected || !offerModal.offerAmount || isNaN(parseFloat(offerModal.offerAmount)) || parseFloat(offerModal.offerAmount) <= 0 || balance < parseFloat(offerModal.offerAmount || '0')}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Package size={16} />
                    Submit Offer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Trade Modal */}
      <RequestTradeModal
        isOpen={requestTradeModal.isOpen}
        onClose={() => setRequestTradeModal({ isOpen: false, item: null })}
        targetPiece={requestTradeModal.item}
        onRequestTrade={handleTradeRequestSubmit}
        onSuccess={handleTradeRequestSuccess}
      />

      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="fixed top-4 right-4 z-[70] animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="bg-green-600 border border-green-500/30 rounded-xl p-4 shadow-lg max-w-sm">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle size={14} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">{successMessage}</p>
              </div>
              <button
                onClick={() => setShowSuccessNotification(false)}
                className="text-white/70 hover:text-white transition-colors flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[70] animate-slide-down">
          <div className={`${
            notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
            notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
            'bg-blue-500/20 border-blue-500/50'
          } border rounded-xl p-4 shadow-xl max-w-md`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'success' ? 'bg-green-500/20' :
                notification.type === 'error' ? 'bg-red-500/20' :
                'bg-blue-500/20'
              }`}>
                {notification.type === 'success' ? (
                  <CheckCircle size={20} className="text-green-400" />
                ) : notification.type === 'error' ? (
                  <AlertCircle size={20} className="text-red-400" />
                ) : (
                  <AlertCircle size={20} className="text-blue-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification({ ...notification, show: false })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Activity; 